import {
  Component,
  EventEmitter,
  h,
  Prop,
  State,
  Event,
  getAssetPath,
  Element,
  Listen,
  Watch,
} from "@stencil/core";
import { navStateStore } from "@phn/state/nav-state-store";
import { dealerSearchResultsStore } from "@phn/state/dealer-search-results-store";
import { getConfig, getHTMLInputElement, isString } from "@phn/utility/helper";
import { Prediction } from "@phn/entities/places";

@Component({
  tag: "phn-nd-autocomplete-search-geolocation-field",
  styleUrl: "autocomplete-search-geolocation-field.scss",
  scoped: true,
})
export class AutocompleteSearchGeolocationField {
  @Prop() placeholder!: string;

  @Prop() predictions!: Prediction[];

  @Prop() initialValue = "";

  @Prop() errorState = "";

  @Prop() errorMessage = "";

  @Prop() isGeolocating = false;

  @State() showPredictions = false;

  @State() highlightedPredictionIndex = -1;

  @State() searchterm = this.initialValue;

  @Event() searchInput!: EventEmitter<string>;

  @Event() clearSearch!: EventEmitter<void>;

  @Event() submitSearch!: EventEmitter<Prediction | null>;

  @Event() predictionsToggle!: EventEmitter<boolean>;

  @Event() geolocationClick!: EventEmitter;

  @Element() el!: HTMLElement;

  @Listen("click", { passive: true, target: "window" })
  clickListener(e: MouseEvent): void {
    this.showPredictions =
      e.composedPath()[0] === this.el.querySelector("input.autofocus") &&
      this.predictions.length > 0;
  }

  @Listen("keydown", { passive: false })
  keydownListener(e: KeyboardEvent): void {
    if (e.key === "Escape" && this.showPredictions) {
      e.preventDefault();
      e.stopPropagation();
      this.showPredictions = false;
    }
  }

  @Watch("showPredictions")
  watchShowPredictions(newValue: boolean): void {
    this.predictionsToggle.emit(newValue);
  }

  @Watch("initialValue")
  watchInitialValue(): void {
    this.searchterm = this.initialValue;
  }

  private onKeyDown(pressedKey: string): void {
    if (this.predictions.length === 0) return;

    switch (pressedKey) {
      case "ArrowDown":
        if (!this.showPredictions) {
          return;
        }
        if (this.highlightedPredictionIndex === this.predictions.length - 1) {
          this.highlightedPredictionIndex = 0;
          break;
        }
        this.highlightedPredictionIndex++;
        break;

      case "ArrowUp":
        if (!this.showPredictions) {
          return;
        }
        if (this.highlightedPredictionIndex < 0) {
          this.highlightedPredictionIndex = 0;
          break;
        } else if (this.highlightedPredictionIndex === 0) {
          this.highlightedPredictionIndex = this.predictions.length - 1;
          break;
        }
        this.highlightedPredictionIndex--;
        break;
      case "Enter": {
        if (this.showPredictions) {
          this.onSubmit();
        }
        break;
      }
    }

    if (this.highlightedPredictionIndex > -1) {
      this.searchterm = this.predictions[this.highlightedPredictionIndex].text;
    }
  }

  private onSubmit() {
    const prediction =
      this.highlightedPredictionIndex < 0
        ? this.predictions[0]
        : this.predictions[this.highlightedPredictionIndex];

    // Do not start a search when the there is no prediction for the search term or when the search term is the same as the previous search term
    if (
      (this.searchterm && !prediction) ||
      (this.searchterm &&
        prediction.text === dealerSearchResultsStore.state.searchTerm)
    ) {
      return;
    }

    this.submitSearch.emit(prediction ?? null);
    this.showPredictions = false;
    if (prediction !== undefined) {
      this.searchterm = prediction.text;
      dealerSearchResultsStore.state.searchTerm = this.searchterm;
      dealerSearchResultsStore.state.predictions = this.predictions;
    }
  }

  render() {
    return (
      <div class="form-container">
        <phn-p-text-field-wrapper
          data-test-id="dealer-search-form-input"
          label={this.placeholder}
          state={isString(this.errorMessage) ? "error" : "none"}
          message={this.errorMessage}
          action-icon="locate"
          action-loading={this.isGeolocating}
          onAction={() => {
            this.geolocationClick.emit();
          }}
        >
          <input
            class="autofocus"
            aria-label="Find a dealer by typing a city name or zip code"
            type="search"
            placeholder={this.placeholder}
            autocomplete="off"
            role="combobox"
            data-validate="required"
            aria-expanded={(
              this.predictions.length > 0 && this.showPredictions
            ).toString()}
            aria-controls="predictions"
            aria-autocomplete="list"
            aria-activedescendant={
              this.showPredictions && this.highlightedPredictionIndex > -1
                ? String(this.highlightedPredictionIndex)
                : undefined
            }
            disabled={this.isGeolocating}
            value={this.searchterm}
            onInput={(event: Event) => {
              event.preventDefault();

              this.showPredictions = true;
              this.highlightedPredictionIndex = -1;

              const value = getHTMLInputElement(event.target)?.value;

              if (!isString(value)) {
                this.searchterm = "";
                this.showPredictions = false;
                this.clearSearch.emit();
                return;
              }

              this.searchInput.emit(value);
            }}
            onKeyDown={(event: KeyboardEvent) => {
              this.onKeyDown(event.key);
            }}
          />
        </phn-p-text-field-wrapper>

        {this.predictions.length > 0 && this.showPredictions ? (
          <div class="prediction-container">
            <ul
              id="predictions"
              data-test-id="dealer-search-autocomplete-predictions"
              role="listbox"
              aria-label="List of location predictions"
            >
              {this.predictions.map((prediction, index) => (
                <li
                  key={prediction.id}
                  class={
                    index === this.highlightedPredictionIndex
                      ? "highlighted-prediction"
                      : ""
                  }
                  role="option"
                  aria-selected={String(
                    index === this.highlightedPredictionIndex,
                  )}
                  id={String(index)}
                  onClick={() => {
                    this.highlightedPredictionIndex = index;
                    this.onSubmit();
                  }}
                >
                  <phn-p-text color="inherit">{prediction.text}</phn-p-text>
                </li>
              ))}
            </ul>
            <img
              class="powered-by-google"
              src={getAssetPath(
                `${getConfig(navStateStore.state.env).ASSETS_URL}/icon-assets/powered_by_google_on_white.png`,
              )}
              alt="powered by Google"
            />
          </div>
        ) : null}
      </div>
    );
  }
}
